import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Checkbox from "@/components/Checkbox";
import { useMutation } from "@apollo/client";
import Button from "@/components/Button";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { JobPreferencesFormData } from "../job-preferences";
import { jobCategories, jobSearchStatuses, minimumCompensationOptions } from "../_constants";
import { CREATE_JOB_PREFERENCES_MUTATION } from "../queries";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import Select from "@/components/Select";
import { LOCATION_PREFERENCES } from "@/lib/usStateHelpers";
import MultiSelect from "@/components/MultiSelect";

const DEFAULT_VALUES = {
  jobSearchStatus: "Not looking for a new position",
  optedInToJobSeekerEmails: false,
  jobCompensationMinimum: "0",
  openToSeasonJobs: false,
  locationPreferences: [] as string[],
  interestInInternationalPositions: false,
  jobCategories: [] as string[]
};

interface CreateJobPreferencesProps {
  setCreatingJobPreferences: (arg: boolean) => void;
  refetchPreferences: () => void;
}

export default function CreateJobPreferences({
  setCreatingJobPreferences,
  refetchPreferences
}: CreateJobPreferencesProps) {
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageCareerPreferences });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { pgaId } = useParams();

  const [createJobPreferences] = useMutation(CREATE_JOB_PREFERENCES_MUTATION, {
    onCompleted: (data) => {
      setIsSubmitting(false);
      if (data.createJobPreferences.success) {
        showSnackbar("Job preferences created");
        setCreatingJobPreferences(false);
        refetchPreferences();
      } else {
        showErrorSnackbar(data.createJobPreferences.message || "Failed to create job preferences");
      }
    },
    onError: () => {
      showErrorSnackbar("Job preferences not created", { subtitle: "Please try again later." });
      setIsSubmitting(false);
    }
  });

  const form = useForm({
    defaultValues: DEFAULT_VALUES
  });

  const { reset } = form;

  const handleSubmit = async (formData: JobPreferencesFormData) => {
    setIsSubmitting(true);

    const input = {
      pgaId: pgaId,
      jobSearchStatus: formData.jobSearchStatus,
      optedInToJobSeekerEmails: formData.optedInToJobSeekerEmails,
      jobCompensationMinimum: formData.jobCompensationMinimum ? parseInt(formData.jobCompensationMinimum) : 0,
      openToSeasonJobs: formData.openToSeasonJobs,
      locationPreferences: formData.locationPreferences,
      interestInInternationalPositions: formData.interestInInternationalPositions,
      jobCategories: formData.jobCategories
    };

    await createJobPreferences({ variables: { input } });
  };

  const handleCancelCreate = () => {
    // Reset form to original values from data
    reset({ ...DEFAULT_VALUES });
    setCreatingJobPreferences(false);
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-2">
        <div className="!h-fit lg:col-span-1">
          <p className={"mb-6 text-title-large-bold font-serif"}>General Preferences</p>
          <div className="space-y-6 w-full">
            <div className="col-span-full">
              <Controller
                control={form.control}
                name="jobSearchStatus"
                render={({ field }) => (
                  <Select
                    label="Job Search Status"
                    options={jobSearchStatuses}
                    value={field.value}
                    onSelect={(value: string) => field.onChange(value)}
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <Controller
                control={form.control}
                name="optedInToJobSeekerEmails"
                render={({ field }) => (
                  <Select
                    label="Email Notifications"
                    options={[
                      { label: "Opt in", value: "opted_in" },
                      { label: "Opt out", value: "opted_out" }
                    ]}
                    value={field.value ? "opted_in" : "opted_out"}
                    onSelect={(value: string) => field.onChange(value === "opted_in")}
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <Controller
                control={form.control}
                name="jobCompensationMinimum"
                render={({ field }) => (
                  <Select
                    label="Compensation Minimum"
                    options={minimumCompensationOptions}
                    value={field.value?.toString() || "0"}
                    onSelect={(value: string) => field.onChange(value)}
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <Controller
                control={form.control}
                name="openToSeasonJobs"
                render={({ field }) => (
                  <Select
                    label="Open to Seasonal Positions"
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" }
                    ]}
                    value={field.value ? "yes" : "no"}
                    onSelect={(value: string) => field.onChange(value === "yes")}
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <Controller
                control={form.control}
                name="locationPreferences"
                render={({ field }) => (
                  <MultiSelect
                    label="Location"
                    options={LOCATION_PREFERENCES.map((loc: string) => ({ label: loc, value: loc }))}
                    selectedValues={field.value}
                    onSelect={(value: string, checked: boolean) => {
                      const currentLocations = field.value || [];
                      const newLocations = checked
                        ? [...currentLocations, value]
                        : currentLocations.filter((l: string) => l !== value);
                      field.onChange(newLocations);
                    }}
                    onClear={() => field.onChange([])}
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <Controller
                control={form.control}
                name="interestInInternationalPositions"
                render={({ field }) => (
                  <Select
                    label="Interested in International Positions"
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" }
                    ]}
                    value={field.value ? "yes" : "no"}
                    onSelect={(value: string) => field.onChange(value === "yes")}
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 lg:col-span-2 lg:pl-6">
          <div className="space-y-3 w-full">
            <p className={"mb-2 text-title-large-bold font-serif"}>Job Categories</p>
            <div className="grid grid-cols-1 gap-y-1 sm:grid-cols-2 sm:[grid-template-rows:repeat(11,minmax(0,auto))] sm:grid-flow-col">
              {jobCategories.map((category: string) => (
                <div key={category} className="flex items-start space-x-2 w-full min-h-[40px]">
                  <Checkbox
                    name={`jobCategories.${category}`}
                    checked={form.watch("jobCategories")?.includes(category)}
                    onChange={() => {
                      const currentCategories = form.watch("jobCategories") || [];
                      const newCategories = currentCategories.includes(category)
                        ? currentCategories.filter((c: string) => c !== category)
                        : [...currentCategories, category];
                      form.setValue("jobCategories", newCategories);
                    }}
                  />
                  <span className="break-words text-sys-brand-on-surface text-body-large leading-5">{category}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outlined" disabled={isSubmitting} onClick={handleCancelCreate}>
          Cancel
        </Button>
        <Button
          disabled={isSubmitting}
          icon={isSubmitting ? "progress_activity" : undefined}
          iconClassName={classNames(isSubmitting && "animate-spin w-6 h-6 !block")}
          variant="filled"
          onClick={() => handleSubmit(form.getValues())}
        >
          Save
        </Button>
      </div>
    </>
  );
}
